import { ReactNode, Children, isValidElement, cloneElement } from 'react'
import { clsx } from 'clsx'
import type { MarqueeSpeed } from './MarqueeRow'

interface MarqueeColumnProps {
  /** Items to scroll vertically. Will be duplicated internally for seamless loop. */
  children: ReactNode
  speed?: MarqueeSpeed
  /** Reverse direction (top→bottom instead of bottom→top) */
  reverse?: boolean
  /** Visible viewport height in px */
  height: number
  /** Pause animation when hovering anywhere over the marquee */
  pauseOnHover?: boolean
  /** Gap between items in pixels (default 16) */
  gap?: number
  className?: string
}

const DURATION_MAP: Record<MarqueeSpeed, string> = {
  slow: '60s',
  normal: '40s',
  fast: '25s',
}

export function MarqueeColumn({
  children,
  speed = 'normal',
  reverse = false,
  height,
  pauseOnHover = false,
  gap = 16,
  className,
}: MarqueeColumnProps) {
  const items = Children.toArray(children)

  const renderItems = (keyPrefix: string) =>
    items.map((child, idx) =>
      isValidElement(child)
        ? cloneElement(child, { key: `${keyPrefix}-${idx}` })
        : child
    )

  return (
    <div
      className={clsx('relative overflow-hidden w-full', className)}
      style={{ height: `${height}px` }}
    >
      <div
        className={clsx(
          'flex flex-col h-max',
          pauseOnHover && 'hover:[animation-play-state:paused]'
        )}
        style={{
          gap: `${gap}px`,
          animationName: 'qb-marquee-vertical',
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          animationDuration: DURATION_MAP[speed],
          animationDirection: reverse ? 'reverse' : 'normal',
        }}
      >
        <div className="flex flex-col shrink-0" style={{ gap: `${gap}px` }}>
          {renderItems('a')}
        </div>
        <div
          aria-hidden
          className="flex flex-col shrink-0"
          style={{ gap: `${gap}px` }}
        >
          {renderItems('b')}
        </div>
      </div>
    </div>
  )
}
